import React, { useEffect, useState } from "react";

import { useAtom } from "jotai";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import * as yup from "yup";

import { API, graphqlOperation } from "@aws-amplify/api";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  appLoadingAtom,
  cancelInProgressAtom,
  cancelResultAtom,
  currentCancelStepAtom,
  currentOrderAtom,
  fetchOrderById,
  loadableCancelRasonsAtom,
  refundOrderLineItemsAtom,
  rejectedOrder,
  selectedItemsAtom,
  selectedOrderIdAtom,
} from "../atoms/atoms";
import { customerAccessTokenAtom } from "../atoms/authAtoms";
import Box from "../components/Box";
import Button from "../components/Button";
import ItemList from "../components/ItemList";
import Label from "../components/Label";
import OrderSelect from "../components/OrderSelect";
import { ResultHeader } from "../components/ResultHeader";
import Select from "../components/Select";
import Spinner from "../components/Spinner";
import Table from "../components/Table";
import TextArea from "../components/TextArea";
import TextField from "../components/TextField";
import {
  LineItemInput,
  OrderItemsCancelMutation,
  OrderItemsCancelMutationVariables,
} from "../graphql/api";
import { orderItemsCancelMutation } from "../graphql/mutations";
import { LineItem, Refund } from "gpc-shared-types";

const schema = yup
  .object({
    reason: yup.string().required(),
    resolution: yup.string().required(),
    comments: yup.string(),
  })
  .required();

export const ReturnCancel = () => {
  const [intention, setIntention] = React.useState<"return" | "cancel">(
    "cancel"
  );
  const [selectedItems, setSelectedItems] = useAtom(selectedItemsAtom);
  const { id: orderId } = useParams();
  const [step, setStep] = useState(1);
  const [, setOrderId] = useAtom(selectedOrderIdAtom);
  const [cancelValue] = useAtom(loadableCancelRasonsAtom);
  const [cancelResult, setCancellationResult] = useAtom(cancelResultAtom);
  const [customerAccessToken] = useAtom(customerAccessTokenAtom);
  const [rejectCancellation, setRejectedCancellation] = useAtom(rejectedOrder);
  const [returnResult, setReturnResult] = useState<Refund>();
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrormessage] = useState<string>("");

  const [appLoading, setAppLoading] = useAtom(appLoadingAtom);
  const [, fetchOrder] = useAtom(fetchOrderById);
  const [order] = useAtom(currentOrderAtom);
  const [cancelInProgress, setCancelInProgress] = useAtom(cancelInProgressAtom);
  const [currentCancelStep, setCurrentCancelStep] = useAtom(
    currentCancelStepAtom
  );
  const [, refundOrderLineItems] = useAtom(refundOrderLineItemsAtom);

  useEffect(() => {
    setAppLoading(true);
    fetchOrder(orderId);
  }, []);

  const formMethods = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: {
      resolution: "Refund",
    },
  });
  setOrderId(orderId);

  const toggleItemSelection = (id: string, selected: boolean) => {
    console.log("toggle", id, selected, selectedItems);

    if (!order) {
      return;
    }

    const selectedItem = (order.lineItems || []).find(
      (item: any) => item && item.id === id
    );

    if (selected) {
      if (!selectedItem) {
        return;
      }
      setSelectedItems([...selectedItems, selectedItem]);
    } else {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    }
  };

  const submitOrderItemsCancel = async (
    formData: yup.InferType<typeof schema>,
    itemsToCancel: LineItem[]
  ) => {
    console.log("Submitting Cancellation", itemsToCancel);
    const cancelLineItems = itemsToCancel.map((item) => ({
      id: item.id,
      quantity: item.currentQuantity,
    }));

    try {
      let queryResult = await refundOrderLineItems(
        `gid://shopify/Order/${orderId}`,
        cancelLineItems,
        formData.reason,
        formData.comments || "",
        formData.resolution
      );

      if (queryResult) {
        setReturnResult(queryResult?.data?.refundOrder);
      }
    } catch (error: any) {
      setIsError(true);
      setCancelInProgress(false);
      if (error?.errors[0]?.message) {
        setErrormessage(error.errors[0].message);
      } else {
        setErrormessage("Something went wrong. Please try again!");
      }
    }
  };

  if (cancelValue.state === "loading") {
    return <Spinner />;
  }

  if (cancelValue.state === "hasError") {
    return <div>error occured</div>;
  }

  // const currency = cancelResult?.canceledItems[0].currency || "USD";
  const currency = order?.totalPriceSet.presentmentMoney.currencyCode || "USD";
  const totalAmount = selectedItems.reduce(
    (acc, item) => acc + parseFloat(item.variant.price) * item.quantity,
    0
  );
  let selectedItemsTitle = selectedItems.map((item) => item.title).toString();
  selectedItemsTitle =
    " ''" + selectedItemsTitle.split(",").join("'', ''") + "''";

  if (appLoading) {
    return <Spinner />;
  }

  if (!order) {
    return <div>Order not found</div>;
  }

  console.log("Order Data :", order);

  if (cancelInProgress) {
    return (
      <div>
        <h2 className="gpc__page-title">Cancellation in progress</h2>
        <Spinner />
      </div>
    );
  }

  return (
    <div className="gpc__return-cancel">
      {[1, 2].includes(step) && (
        <>
          <h2 className="gpc__page-title">Return or cancel items</h2>
          <div className="gpc__return-cancel__content">
            <div className="gpc__return-cancel__wizard">
              {step === 1 && (
                <Box>
                  <div>
                    <Label>I want to:</Label>
                    <div className="gpc__button-group">
                      {/* <Button
                          active={intention === "return"}
                          onClick={() => setIntention("return")}
                        >
                          Return an item
                        </Button> */}
                      <Button
                        active={intention === "cancel"}
                        onClick={() => setIntention("cancel")}
                      >
                        Cancel an item
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Choose item(s) to return:</Label>
                    <ItemList
                      items={order?.lineItems || []}
                      selectedItems={selectedItems}
                      onSelectionChange={toggleItemSelection}
                    />
                  </div>
                </Box>
              )}
              {step === 2 && (
                <Box>
                  <Controller
                    name="reason"
                    control={formMethods.control}
                    render={({ field: { onChange, value } }) => (
                      <div>
                        <Label>Why are you cancelling these items?</Label>
                        <Select
                          items={
                            cancelValue.state === "hasData"
                              ? cancelValue.data
                              : []
                          }
                          onChange={onChange}
                          selectedItem={value}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    name="resolution"
                    control={formMethods.control}
                    render={({ field: { onChange, value } }) => (
                      <div>
                        <Label>Preferred resolution</Label>
                        <TextField
                          onChange={onChange}
                          value={value}
                          disabled
                          helpText="Once approved, you will receive a full refund within 5 business days to the payment method used for the order."
                        />
                      </div>
                    )}
                  />
                  <Controller
                    name="comments"
                    control={formMethods.control}
                    render={({ field: { onChange, value } }) => (
                      <div>
                        <Label>Comments</Label>
                        <TextArea onChange={onChange} value={value} />
                      </div>
                    )}
                  />
                </Box>
              )}
            </div>
            <div className="gpc__return-cancel__selected-items">
              <OrderSelect
                items={selectedItems || []}
                buttonLabel={step === 2 ? "Submit" : "Continue"}
                onButtonClick={() => {
                  if (selectedItems.length > 0 && step === 2) {
                    setStep(3);

                    formMethods.handleSubmit((data) =>
                      submitOrderItemsCancel(data, selectedItems)
                    )();
                  } else if (selectedItems.length > 0 && step === 1) {
                    setStep(2);
                  } else {
                    setStep(1);
                  }
                }}
              />
            </div>
          </div>
        </>
      )}
      {step === 3 && (
        <div>
          <h2 className="gpc__page-title">Cancellation Results</h2>
          <Box>
            {isError ? (
              <div className="gpc__return-cancel__container">
                <ResultHeader status="failure">Request Declined</ResultHeader>
                <div className="gpc__return-cancel__message">
                  Unfortunately your request to cancel {selectedItemsTitle} from
                  your order has been declined.
                </div>
              </div>
            ) : (
              <div className="gpc__return-cancel__container">
                <ResultHeader status="success">Items cancelled</ResultHeader>
                <div className="gpc__return-cancel__message">
                  {selectedItemsTitle} have been removed from your order, and
                  you will no longer receive this item.
                </div>
              </div>
            )}

            {isError && (
              <div>
                <span style={{ fontWeight: "700" }}>Reason: </span>
                <span>{errorMessage}</span>
              </div>
            )}

            <Table>
              <tbody>
                <tr>
                  <th>Request status</th>
                  <td>Completed</td>
                </tr>
                <tr>
                  <th>Reason for cancellation</th>
                  <td>
                    {formMethods.getValues("reason") || "No reason specified"}
                  </td>
                </tr>
                <tr>
                  <th>Resolution</th>
                  <td>{isError ? errorMessage : 'Refund'}</td>
                </tr>
                <tr>
                  <th>Additional comments</th>
                  <td>
                    {formMethods.getValues("comments") ||
                      "No additional comments provided"}
                  </td>
                </tr>
              </tbody>
            </Table>

            <Table>
              <tbody className="gpc__desktop-table">
                <tr>
                  <th>Product name</th>
                  <th>Status</th>
                  <th>Quantity</th>
                  <th>Amount</th>
                </tr>
                {selectedItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.title}</td>
                    <td>Cancelled</td>
                    <td>{item.quantity}</td>
                    <td>
                      {currency}{" "}
                      {(parseFloat(item.variant.price) * item.quantity).toFixed(
                        2
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td></td>
                  <td></td>
                  <th>Product refund</th>
                  <td>
                    {currency}{" "}
                    {returnResult?.totalRefundedSet?.presentmentMoney?.amount ||
                      "0.0"}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <th>Delivery refund</th>
                  <td>
                    {currency} {returnResult?.shippingRefundSet.amount || "0.0"}
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <th>Total refund</th>
                  <td>
                    {currency}{" "}
                    {(
                      parseFloat(
                        returnResult?.totalRefundedSet?.presentmentMoney
                          ?.amount || "0.0"
                      ) +
                      parseFloat(
                        returnResult?.shippingRefundSet.amount || "0.0"
                      )
                    ).toFixed(2)}
                  </td>
                </tr>
              </tbody>
              <tbody className="gpc__mobile-table">
                {/* {cancelResult?.canceledItems.map((item) => (
                    <tr key={item.id}>
                      <th>Product name</th>
                      <td>{item.title}</td>
                      <th>Status</th>
                      <td>Cancelled</td>
                      <th>Quantity</th>
                      <td>{item.quantity}</td>
                      <th>Amount</th>

                      <td>
                        {item.currency}{" "}
                        {parseFloat(item.price) * parseInt(item.quantity)}
                      </td>
                    </tr>
                  ))} */}
                {selectedItems.map((item) => (
                  <tr key={item.id}>
                    <th>Product name</th>
                    <td>{item.title}</td>
                    <th>Status</th>
                    <td>Cancelled</td>
                    <th>Quantity</th>
                    <td>{item.quantity}</td>
                    <th>Amount</th>

                    <td>
                      {currency}{" "}
                      {parseFloat(item.variant.price) * item.quantity}
                    </td>
                  </tr>
                ))}
                <tr>
                  <th>Product refund</th>
                  <td>
                    {currency}{" "}
                    {returnResult?.totalRefundedSet?.presentmentMoney?.amount ||
                      "0.0"}
                  </td>
                </tr>
                <tr>
                  <th>Delivery refund</th>
                  <td>
                    {currency} {returnResult?.shippingRefundSet.amount || "0.0"}
                  </td>
                </tr>
                <tr>
                  <th>Total refund</th>
                  <td>
                    {currency}{" "}
                    {(
                      parseFloat(
                        returnResult?.totalRefundedSet?.presentmentMoney
                          ?.amount || "0.0"
                      ) +
                      parseFloat(
                        returnResult?.shippingRefundSet.amount || "0.0"
                      )
                    ).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </Table>

            {/* <Button active onClick={() => setStep(1)}>
            Back to orders
          </Button> */}
          </Box>
        </div>
      )}
    </div>
  );
};

export default ReturnCancel;
